import React from 'react';

/**
 * Allows to merge multiple refs into a single ref.
 *
 * @example
 * ```tsx
 * const inputRef = useRef<HTMLInputElement>(null);
 * const inputRef2 = useRef<HTMLInputElement>(null);
 * const [inputElement, setInputElement = useState<HTMLInputElement|null>();
 *
 * <input ref={mergeRefs(inputRef, inputRef2, setInputElement)} />
 * ```
 *
 * @param refs
 * @returns
 */
export function mergeRefs<T>(
  ...refs: (React.ForwardedRef<T> | null | undefined)[]
): (element: T) => void {
  return (element: T): void => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(element);
      }

      if (typeof ref === 'object' && ref !== null) {
        ref.current = element;
      }
    });
  };
}

/**
 *
 * > To trigger a React’s change event handler registered on an input element, you should set the value property on the element using the native setter before dispatching the event
 *
 * - https://github.com/facebook/react/issues/19846
 * - https://github.com/green-labs/formula-design-system/pull/106
 * - https://stackoverflow.com/questions/23892547/what-is-the-best-way-to-trigger-change-or-input-event-in-react-js
 *
 * @param element
 * @param value
 */

export function dispatchEventInput(
  element: HTMLInputElement,
  value: string,
): void {
  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    'value',
  )?.set;

  nativeInputValueSetter?.call(element, value);

  const event = new Event('input', { bubbles: true });
  element.dispatchEvent(event);
}
